import { Command } from 'cadenza/api-client/workbook-commands';
import type { MediaItemName } from 'cadenza/repository/model/media-item-name.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import type { SignedMediaItemIdWithProviderId } from 'cadenza/ui/image-browser/media-items-provider';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import { isDataViewLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import type { ExtendedLayerNode, WorkbookLayerTreeModel } from 'cadenza/workbook/state/workbook-layer-tree-model';
import type { View } from 'cadenza/workbook/view/view';
import { getLayerGroupsFromView, getViewById, layerById } from 'cadenza/workbook/workbook-core-selectors.state';
import { getLayerSetting } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export function isReferenceGroupLayer(workbookLayerDto: WorkbookLayerDto) {
  return isReferenceLayer(workbookLayerDto) && isGroupOrToggleLayer(workbookLayerDto);
}

export function isReferenceLayer({ settings }: WorkbookLayerDto) {
  return Boolean(
    settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_REPOSITORY_NAME')] &&
      settings[LayerSettingsKey.toPersistenceKey('CATALOG_LAYER_ITEM_NAME')],
  );
}

export function isGroupOrToggleLayer(workbookLayerDto: WorkbookLayerDto) {
  return workbookLayerDto.structureType === 'GROUP' || workbookLayerDto.structureType === 'TOGGLE';
}

/**
 * returns data views which are not referenced or indirectly referenced by having a parent group layer
 * which is a referenced layer
 */
export function getUnreferencedDataViews(view: View) {
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  const viewDto = getViewById(getStore().getState(), view.viewId);
  return view.dataViews.filter((dataView) => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return layer && isUnreferencedLayer(layer, view.viewId);
  });
}

/**
 * returns data views which are not inside referenced groups.
 * referenced data views will be included
 */
export function getDataViewsNotWithinReferenceGroups(viewId: ViewId) {
  const view = getViewById(getStore().getState(), viewId);
  if (view.type !== 'MAP') {
    return view.dataViews;
  }
  return view.dataViews.filter((dataViewId) => {
    // filter out layers in reference groups
    return !isLayerInsideViewReferenceGroupLayer(viewId, dataViewId);
  });
}

/**
 * returns data views which are directly referenced,
 * meaning there are not referenced through a referenced parent group
 */
function getDirectReferencedDataViews(view: View) {
  if (view.type !== 'MAP') {
    return [];
  }
  const viewDto = getViewById(getStore().getState(), view.viewId);
  return view.dataViews.filter((dataView) => {
    const layer = viewDto.layerDefinitions[dataView.dataViewId];
    return isReferenceLayer(layer);
  });
}

export function getDataViewAnalysisContextDataViews(view: View) {
  return [...getUnreferencedDataViews(view), ...getDirectReferencedDataViews(view)];
}

/**
 * returns true if this layer is not referenced or indirectly referenced by having a parent group
 * layer which is a referenced layer
 */
export function isUnreferencedLayer(layer: WorkbookLayerDto, viewId: ViewId) {
  return !isReferenceLayer(layer) && !isLayerInsideViewReferenceGroupLayer(viewId, layer.id);
}

function isLayerInsideViewReferenceGroupLayer(viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(getStore().getState(), viewId);
  return viewDto.layerTree.some((node) => isLayerInsideReferenceGroupLayer(viewDto, layerId, node));
}

function isLayerInsideReferenceGroupLayer(viewDto: ViewDto, layerId: LayerId, node: WorkbookLayerTreeNode): boolean {
  const nodeIsReferenceLayer = isReferenceLayer(viewDto.layerDefinitions[node.id]);
  if (nodeIsReferenceLayer) {
    return isLayerInsideNode(node, layerId);
  }
  return node.children.some((child) => isLayerInsideReferenceGroupLayer(viewDto, layerId, child));
}

export function getReferenceGroupsWithDataViews(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  if (getViewById(state, viewId).type !== 'MAP') {
    return [];
  }
  const groups = getLayerGroupsFromView(state, viewId);
  return groups.filter((group) => isReferenceLayer(group) && checkGroupForDataViews(state, viewId, group.id));
}

function checkGroupForDataViews(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(state, viewId);
  const node = viewDto.layerTree.find((layerTree) => layerTree.id === layerId);
  return node && isDataViewInNode(viewDto, node);
}

function isDataViewInNode(view: ViewDto, node: WorkbookLayerTreeNode): boolean {
  const layerDefinition = view.layerDefinitions[node.id];
  if (isDataViewLayer(layerDefinition)) {
    return true;
  }
  return node.children.some((layerTreeNode) => isDataViewInNode(view, layerTreeNode));
}

export function isReferenceLayerById(viewId: ViewId, layerId: LayerId) {
  const viewDto = getViewById(getStore().getState(), viewId);
  return isDataViewReferenceByViewDto(viewDto, layerId);
}

export function isDataViewReferenceByViewDto(viewDto: ViewDto, layerId: LayerId) {
  // layers can only be in map view
  if (viewDto.type !== 'MAP') {
    return false;
  }
  const layerFromDefinition = Object.values(viewDto.layerDefinitions).find((layer) => layer.id === layerId);
  return Boolean(layerFromDefinition && isReferenceLayer(layerFromDefinition));
}

function isLayerInsideNode(parentLayer: WorkbookLayerTreeNode, layerId: LayerId): boolean {
  return parentLayer.children.some((child) => {
    return child.children.length > 0 ? isLayerInsideNode(child, layerId) : child.id === layerId;
  });
}

export function sortDataViewIdsBasedOnLayerTree(viewId: ViewId, dataViewIds: DataViewId[]): DataViewId[] {
  const view = getViewById(getStore().getState(), viewId);
  const layerTree = view.layerTree;
  if (!layerTree) {
    return dataViewIds;
  }
  return flatten(view.layerTree)
    .map((layerTreeNode) => layerTreeNode.id)
    .filter((layerId) => dataViewIds.includes(layerId as DataViewId))
    .map((layerId) => layerId as DataViewId);
}

function flatten(layerTreeNodes: WorkbookLayerTreeNode[]): WorkbookLayerTreeNode[] {
  return layerTreeNodes.flatMap((layerTreeNode) => [layerTreeNode, ...flatten(layerTreeNode.children)]);
}

export function getSortedLayerIds(viewId: ViewId) {
  const view = getViewById(getStore().getState(), viewId);
  if (view.type !== 'MAP') {
    throw new Error('The view is not a map.');
  }
  return flatten(view.layerTree).map((layerTreeNode) => layerTreeNode.id);
}

export function getSignedMediaItemWithProviderId(
  layerDto: WorkbookLayerDto,
): SignedMediaItemIdWithProviderId | undefined {
  const mediaItemName = getLayerSetting<MediaItemName>(layerDto, 'LAYER_LEGEND_IMAGE_CONTENT_IDENTIFIER');
  if (mediaItemName) {
    return {
      providerId: 'workbook',
      mediaItemId: {
        repositoryName: getLayerSetting<RepositoryName>(layerDto, 'LAYER_LEGEND_IMAGE_REPOSITORY')!,
        itemName: mediaItemName,
        signature: getLayerSetting<string>(layerDto, 'LAYER_LEGEND_IMAGE_SIGNATURE')!,
      },
    };
  }
}

export function createChangeVisibilityCommandForLayersInPath(
  viewId: ViewId,
  layerTreeModel: WorkbookLayerTreeModel,
  layerNode: ExtendedLayerNode,
  visible: boolean,
) {
  const changeLayerVisibilityCommand = createChangeLayerVisibilityCommand(viewId, layerNode, visible);
  const parentId = layerNode.parent?.id;

  if (parentId && !layerTreeModel.isRootNode(parentId) && visible && !layerTreeModel.isLayerNodeVisible(parentId)) {
    const commands = [changeLayerVisibilityCommand];
    layerTreeModel
      .getAllAncestors(layerNode.id)
      .forEach((ancestor) => commands.push(createChangeLayerVisibilityCommand(viewId, ancestor, visible)));
    return Command.COMPOSITE_COMMAND({ commands });
  }
  return changeLayerVisibilityCommand;
}

function createChangeLayerVisibilityCommand(viewId: ViewId, layerNode: ExtendedLayerNode, visible: boolean) {
  const parentId = layerNode.parent?.id;
  if (parentId) {
    const workbookLayerDto = layerById(getStore().getState(), viewId, parentId);
    if (workbookLayerDto && workbookLayerDto.structureType === 'TOGGLE') {
      return Command.CHANGE_LAYER_SETTINGS({
        viewId,
        layerId: parentId,
        settings: { [LayerSettingsKey.toPersistenceKey('CLASSIC_TOGGLE_LAYER_ACTIVE_THEME_ID')]: layerNode.id },
      });
    }
  }
  return Command.CHANGE_LAYER_SETTINGS({
    viewId,
    layerId: layerNode.id,
    settings: { [LayerSettingsKey.toPersistenceKey('VISIBLE')]: Boolean(visible).toString() },
  });
}

/**
 * Returns parent layer if for a layer only if the parent layer exists.
 * It does not return 'view_root_...' parent id added in {@link createViewTreeModel}
 *
 * @param layerNode
 */
export function getParentLayerId(layerNode: ExtendedLayerNode) {
  return layerNode.depth > 2 ? layerNode.parent?.id : undefined;
}
